import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Truck,
  User,
  Phone,
  Calendar,
  Package,
  MapPin,
  Building,
  CheckCircle2,
  Clock,
  UserCheck,
  RotateCcw,
  FileText,
  Download,
  Factory,
  Warehouse,
  Send,
  Layers,
  Edit2,
  X,
} from "lucide-react";
import { api, downloadDocument } from "../../lib/api";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import { money, compactMoney } from "../../lib/format";
import MoveBackStageModal from "./MoveBackStageModal";
import OrderHistoryTrail from "./OrderHistoryTrail";

function formatDateTime(val) {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(val);
  }
}

export default function DispatchDetail({ orderId, onBack, onChange, user }) {
  const [order, setOrder] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const [client, setClient] = useState(null);
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");

  // Delivery agent assignment form
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [customAgentName, setCustomAgentName] = useState("");
  const [customAgentPhone, setCustomAgentPhone] = useState("");
  const [isChangingAgent, setIsChangingAgent] = useState(false);

  // Out for Delivery modal / state
  const [showOutForDeliveryModal, setShowOutForDeliveryModal] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [transporterName, setTransporterName] = useState("");

  // Batch Modals
  const [showBatchAssignModal, setShowBatchAssignModal] = useState(false);
  const [showBatchOutModal, setShowBatchOutModal] = useState(false);
  const [movingBack, setMovingBack] = useState(false);

  const canAct = isAdmin(user) || canAccessDepartment(user, ["Production", "Delivery", "Sales"]);
  const canSeePhone = canViewClientPhone(user);
  const isBatchMode = Boolean(batchData && batchData.orders && batchData.orders.length > 1);

  // 1. Load order / batch details
  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      const isDirectBatch = typeof orderId === "string" && orderId.toUpperCase().startsWith("BATCH-");

      if (isDirectBatch) {
        const batchRes = await api("get", `/dispatch/batch/${orderId}`);
        const bData = batchRes.data;
        setBatchData(bData);
        if (bData.orders && bData.orders.length > 0) {
          setOrder(bData.orders[0]);
          setTrackingNumber(bData.orders[0].tracking_number || "");
        }
        if (bData.client) {
          setClient(bData.client);
        }
        return;
      }

      // Load single order
      const res = await api("get", `/orders/${orderId}`);
      const data = res.data;
      setOrder(data);
      setTrackingNumber(data.tracking_number || "");

      // If part of bulk batch, load batch context
      if (data.upload_batch_id) {
        try {
          const batchRes = await api("get", `/dispatch/batch/${data.upload_batch_id}`);
          if (batchRes.data && batchRes.data.orders && batchRes.data.orders.length > 1) {
            setBatchData(batchRes.data);
          } else {
            setBatchData(null);
          }
        } catch {
          setBatchData(null);
        }
      } else {
        setBatchData(null);
      }

      if (data.client_id) {
        try {
          const clientRes = await api("get", `/clients/${data.client_id}`);
          setClient(clientRes.data);
        } catch {
          // fallback
        }
      }
    } catch (err) {
      console.error("Failed to load dispatch order:", err);
      toast.error("Could not load dispatch details");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // 2. Load delivery staff list
  const loadDeliveryAgents = useCallback(async () => {
    try {
      const res = await api("get", "/users?department=Delivery&active_only=true");
      const list = Array.isArray(res.data) ? res.data : [];
      const validAgents = list.filter(
        (u) =>
          !u.is_deleted &&
          u.is_active !== false &&
          ((u.department || "").toLowerCase() === "delivery" || (u.role || "").toLowerCase() === "delivery")
      );

      const agentMap = new Map();
      validAgents.forEach((a) => {
        const key = (a.name || "").trim().toLowerCase();
        if (key) {
          agentMap.set(key, {
            ...a,
            user_id: a.user_id || a.id || key,
            name: a.name,
            phone: a.phone || "",
          });
        }
      });

      // Also ensure current order and batch orders' delivery persons are included
      const checkOrders = [order, ...(batchData?.orders || [])].filter(Boolean);
      checkOrders.forEach((o) => {
        const pName = (o.assigned_delivery_person || "").trim();
        if (pName) {
          const key = pName.toLowerCase();
          if (!agentMap.has(key)) {
            agentMap.set(key, {
              user_id: o.delivery_person_id || `DEL-${key.replace(/[^a-z0-9]+/g, "-").toUpperCase()}`,
              name: pName,
              phone: o.delivery_person_phone || "",
              department: "Delivery",
              role: "user",
              is_active: true,
            });
          } else if (!agentMap.get(key).phone && o.delivery_person_phone) {
            agentMap.get(key).phone = o.delivery_person_phone;
          }
        }
      });

      setDeliveryAgents(Array.from(agentMap.values()));
    } catch (err) {
      console.error("Failed to load delivery staff:", err);
      setDeliveryAgents([]);
    }
  }, [order, batchData]);

  useEffect(() => {
    loadOrder();
    loadDeliveryAgents();
  }, [loadOrder, loadDeliveryAgents]);

  // Sync selectedAgentId
  useEffect(() => {
    if (order && deliveryAgents.length > 0) {
      if (order.delivery_person_id) {
        const byId = deliveryAgents.find((a) => a.user_id === order.delivery_person_id);
        if (byId) setSelectedAgentId(byId.user_id || byId.id || byId.name);
      }
      if (!selectedAgentId && order.assigned_delivery_person) {
        const matched = deliveryAgents.find(
          (a) => a.name?.toLowerCase() === order.assigned_delivery_person?.toLowerCase()
        );
        if (matched) setSelectedAgentId(matched.user_id || matched.id || matched.name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, deliveryAgents]);

  // Switch active product inside batch
  const handleSelectProduct = (productOrder) => {
    setOrder(productOrder);
    setTrackingNumber(productOrder.tracking_number || "");
    setIsChangingAgent(false);
  };

  // Assign Delivery Person (Single order)
  const handleAssignDeliveryPerson = async (e) => {
    if (e) e.preventDefault();
    if (!order) return;
    let name = "";
    let phone = "";
    let id = "";

    if (selectedAgentId === "custom") {
      name = customAgentName.trim();
      phone = customAgentPhone.trim();
      if (!name) {
        toast.error("Please enter the delivery person's name");
        return;
      }
    } else {
      const agent = deliveryAgents.find((a) => (a.user_id && a.user_id === selectedAgentId) || a.name === selectedAgentId);
      if (!agent) {
        toast.error("Please select a delivery person");
        return;
      }
      name = agent.name;
      phone = agent.phone || "";
      id = agent.user_id || agent.id || "";
    }

    setBusyAction("assign");
    try {
      await api("patch", `/orders/${order.order_id}/assign-delivery`, {
        assigned_delivery_person: name,
        delivery_person_phone: phone,
        delivery_person_id: id,
      });
      toast.success(`Assigned ${name} — Order moved to Delivery!`);
      setIsChangingAgent(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not assign delivery person";
      toast.error(typeof msg === "string" ? msg : "Assignment failed");
    } finally {
      setBusyAction("");
    }
  };

  // Batch Assign Delivery Person
  const handleBatchAssign = async ({ agentId, customName, customPhone, remarks }) => {
    if (!batchData) return;
    let name = "";
    let phone = "";
    let id = "";

    if (agentId === "custom") {
      name = customName.trim();
      phone = customPhone.trim();
      if (!name) {
        toast.error("Please enter delivery person name");
        return;
      }
    } else {
      const agent = deliveryAgents.find((a) => (a.user_id && a.user_id === agentId) || a.name === agentId);
      if (!agent) {
        toast.error("Please select a delivery person");
        return;
      }
      name = agent.name;
      phone = agent.phone || "";
      id = agent.user_id || agent.id || "";
    }

    setBusyAction("batch_assign");
    try {
      await api("patch", `/orders/batch/${batchData.batch_id}/assign-delivery`, {
        assigned_delivery_person: name,
        delivery_person_phone: phone,
        delivery_person_id: id,
        remarks: remarks || "Assigned for bulk delivery",
      });
      toast.success(`Assigned ${name} across all products in Bulk Request #${batchData.batch_id}!`);
      setShowBatchAssignModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not assign delivery person";
      toast.error(typeof msg === "string" ? msg : "Batch assignment failed");
    } finally {
      setBusyAction("");
    }
  };

  // Generate Challan (Single Order)
  const handleGenerateChallan = async (targetOrderId = order?.order_id) => {
    if (!targetOrderId) return;
    setBusyAction(`challan_${targetOrderId}`);
    try {
      const res = await api("post", `/orders/${targetOrderId}/generate-challan`);
      toast.success(`Generated Delivery Challan #${res.data.challan_number}!`);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not generate challan";
      toast.error(typeof msg === "string" ? msg : "Challan generation failed");
    } finally {
      setBusyAction("");
    }
  };

  // Generate Bulk Challan (Batch)
  const handleBatchGenerateChallan = async () => {
    if (!batchData) return;
    setBusyAction("batch_challan");
    try {
      const res = await api("post", `/orders/batch/${batchData.batch_id}/generate-challan`);
      toast.success(`Generated Bulk Delivery Challan #${res.data.challan_number} for all products!`);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not generate bulk challan";
      toast.error(typeof msg === "string" ? msg : "Bulk challan generation failed");
    } finally {
      setBusyAction("");
    }
  };

  // Mark Out for Delivery (Single Order)
  const handleMarkOutForDelivery = async (targetOrderId = order?.order_id) => {
    if (!targetOrderId) return;
    setBusyAction(`out_${targetOrderId}`);
    try {
      await api("post", `/orders/${targetOrderId}/out-for-delivery`, {
        transporter: transporterName.trim() || order?.assigned_delivery_person || "In-House Delivery",
        tracking_number: trackingNumber.trim(),
      });
      toast.success("Order marked Out for Delivery!");
      setShowOutForDeliveryModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not mark out for delivery";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setBusyAction("");
    }
  };

  // Batch Mark Out for Delivery
  const handleBatchOutForDelivery = async ({ transporter, tracking, remarks }) => {
    if (!batchData) return;
    setBusyAction("batch_out");
    try {
      await api("post", `/orders/batch/${batchData.batch_id}/out-for-delivery`, {
        transporter: transporter || batchData.assigned_delivery_person || "In-House Delivery",
        tracking_number: tracking || "",
        remarks: remarks || "Bulk batch out for delivery",
      });
      toast.success(`Bulk Request #${batchData.batch_id} marked Out for Delivery!`);
      setShowBatchOutModal(false);
      await loadOrder();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not mark batch out for delivery";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setBusyAction("");
    }
  };



  if (loading && !order && !batchData) {
    return (
      <div className="content">
        <div className="loading-screen">Loading dispatch details…</div>
      </div>
    );
  }

  if (!order && !batchData) {
    return (
      <div className="content">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={14} /> Back to Dispatch Queue
        </button>
        <div className="panel" style={{ padding: 40, textAlign: "center", marginTop: 20 }}>
          <h3>Dispatch order not found</h3>
          <p className="muted">This order might have been moved or deleted.</p>
        </div>
      </div>
    );
  }

  const isDeliveryPersonAssigned = Boolean(order?.assigned_delivery_person);
  const isOut = order?.dispatch_status === "Out for Delivery" || order?.delivery_status === "Out for Delivery";
  const isDel = order?.delivery_status === "Delivered" || order?.dispatch_status === "Delivered";
  const currentStatus = isDel ? "Delivered" : isOut ? "Out for Delivery" : isDeliveryPersonAssigned ? "Assigned to Delivery" : "Ready for Dispatch";

  const statusBadgeStyle = (st) => {
    switch (st) {
      case "Delivered":
        return { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" };
      case "Out for Delivery":
        return { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" };
      case "Assigned to Delivery":
      case "Assigned":
        return { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" };
      default:
        return { color: "#d97706", bg: "#fffbeb", border: "#fde68a" };
    }
  };

  // Address computation
  const shippingAddress =
    order?.delivery_address ||
    client?.shipping_address ||
    client?.billing_address ||
    order?.client_address ||
    "Standard client address on file";

  const contactPerson =
    order?.client_contact_person ||
    client?.contact_person ||
    order?.client_name ||
    "—";
  const contactPhone = canViewClientPhone(user) ? (order?.client_phone || client?.phone || "") : "";
  const contactEmail = order?.client_email || client?.email || "";

  // Document downloads for Challan
  const challanDoc = (order?.documents || []).find((d) => d.category === "Challan" || d.document_id === order.challan_document_id);

  return (
    <div className="content" style={{ maxWidth: 1280, margin: "0 auto" }}>
      {/* Back Button */}
      <button className="back-btn" onClick={onBack} data-testid="dispatch-back-button">
        <ArrowLeft size={14} /> Back to Dispatch Queue
      </button>

      {/* ============================================================== */}
      {/* BATCH HEADER (When viewing a bulk Excel upload batch)           */}
      {/* ============================================================== */}
      {isBatchMode && (
        <div
          style={{
            background: "#ffffff",
            border: "1.5px solid #cbd5e1",
            borderRadius: 10,
            padding: "16px 20px",
            marginBottom: 20,
            boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
          }}
          data-testid={`bulk-dispatch-batch-header-${batchData.batch_id}`}
        >
          {/* Top Row: Batch ID, Tag & Priority */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  background: "#eff6ff",
                  color: "#2563eb",
                  border: "1px solid #bfdbfe",
                  padding: "2px 8px",
                  borderRadius: 5,
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: "monospace",
                }}
              >
                #{batchData.batch_id}
              </span>
              <span
                style={{
                  background: "#f1f5f9",
                  color: "#475569",
                  border: "1px solid #e2e8f0",
                  padding: "2px 8px",
                  borderRadius: 5,
                  fontSize: 11.5,
                  fontWeight: 700,
                }}
              >
                Bulk Dispatch Request · {batchData.orders.length} Products
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                className={`badge ${
                  batchData.priority === "Urgent" ? "red" : batchData.priority === "High" ? "amber" : "blue"
                }`}
                style={{ fontSize: 11 }}
              >
                {batchData.priority || "Normal"}
              </span>
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  padding: "2px 10px",
                  borderRadius: 5,
                  ...statusBadgeStyle(batchData.dispatch_status),
                }}
              >
                {batchData.dispatch_status}
              </span>
            </div>
          </div>

          {/* Client Title & Subtitle */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ margin: "2px 0 4px", fontSize: 22, color: "var(--navy)", fontWeight: 700 }}>
                {batchData.client?.company_name || batchData.client_name}
              </h1>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: "#4f46e5" }}>
                Bulk Dispatch Request · {batchData.orders.length} Products · {(batchData.total_quantity || 0).toLocaleString()} Total Units
              </div>
              {batchData.client?.contact_person && batchData.client.contact_person !== "—" && (
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                  Contact: {batchData.client.contact_person}
                  {canSeePhone && batchData.client.phone && batchData.client.phone !== "—" && ` · 📞 ${batchData.client.phone}`}
                  {batchData.client.email && batchData.client.email !== "—" && ` · ${batchData.client.email}`}
                </div>
              )}
            </div>

            {/* Batch Level Action Buttons */}
            {canAct && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                <button
                  type="button"
                  className="outline-btn compact"
                  style={{ color: "#b45309", borderColor: "#fde68a", background: "#fffbeb", fontSize: 12 }}
                  onClick={() => setMovingBack(true)}
                  title="Move bulk request back to Production"
                  data-testid="batch-move-back-btn"
                >
                  <RotateCcw size={12} /> Move Back
                </button>
                <button
                  className="outline-btn compact"
                  onClick={() => setShowBatchAssignModal(true)}
                  disabled={Boolean(busyAction)}
                  data-testid="batch-assign-delivery-btn"
                  title="Assign delivery staff across all products in this batch"
                >
                  <UserCheck size={13} /> Assign Delivery Person
                </button>
                <button
                  className="outline-btn compact"
                  onClick={handleBatchGenerateChallan}
                  disabled={Boolean(busyAction)}
                  data-testid="batch-generate-challan-btn"
                  title="Generate Bulk Delivery Challan PDF for all products"
                >
                  <FileText size={13} /> Generate Bulk Challan
                </button>
                {batchData.dispatch_status !== "Out for Delivery" && batchData.dispatch_status !== "Delivered" && (
                  <button
                    className="primary-btn compact"
                    style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
                    onClick={() => setShowBatchOutModal(true)}
                    disabled={Boolean(busyAction)}
                    data-testid="batch-out-for-delivery-btn"
                    title="Mark entire bulk batch out for delivery"
                  >
                    <Truck size={13} /> Mark Out for Delivery
                  </button>
                )}

              </div>
            )}
          </div>

          {/* Status Breakdown Pills */}
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 14,
              paddingTop: 12,
              borderTop: "1px solid #f1f5f9",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>Batch Status:</span>
            {batchData.counts?.["Ready for Dispatch"] > 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "#fffbeb",
                  color: "#d97706",
                  border: "1px solid #fde68a",
                }}
              >
                {batchData.counts["Ready for Dispatch"]} Ready for Dispatch
              </span>
            )}
            {batchData.counts?.["Out for Delivery"] > 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "#f5f3ff",
                  color: "#7c3aed",
                  border: "1px solid #ddd6fe",
                }}
              >
                {batchData.counts["Out for Delivery"]} Out for Delivery
              </span>
            )}
            {batchData.counts?.["Delivered"] > 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "#ecfdf5",
                  color: "#059669",
                  border: "1px solid #a7f3d0",
                }}
              >
                {batchData.counts["Delivered"]} Delivered
              </span>
            )}
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* PRODUCT SELECTOR TABS (Inside Bulk Batch)                     */}
      {/* ============================================================== */}
      {isBatchMode && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#64748b", marginBottom: 8 }}>
            Products in Bulk Request ({batchData.orders.length}) — Click to inspect individually:
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 10,
            }}
          >
            {batchData.orders.map((o) => {
              const isSelected = order?.order_id === o.order_id;
              const isOutP = o.dispatch_status === "Out for Delivery" || o.delivery_status === "Out for Delivery";
              const isDelP = o.delivery_status === "Delivered" || o.dispatch_status === "Delivered";
              const stLabel = isDelP ? "Delivered" : isOutP ? "Out for Delivery" : o.assigned_delivery_person ? "Assigned" : "Ready";
              const bTheme = statusBadgeStyle(stLabel);

              return (
                <div
                  key={o.order_id}
                  onClick={() => handleSelectProduct(o)}
                  style={{
                    cursor: "pointer",
                    background: isSelected ? "#f8fafc" : "#ffffff",
                    border: isSelected ? "2px solid #4f46e5" : "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: "10px 12px",
                    boxShadow: isSelected ? "0 2px 6px rgba(79,70,229,0.15)" : "none",
                    transition: "all 0.15s ease",
                  }}
                  data-testid={`batch-product-tab-${o.order_id}`}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>
                      #{o.order_id}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "1px 6px",
                        borderRadius: 3,
                        color: bTheme.color,
                        background: bTheme.bg,
                        border: `1px solid ${bTheme.border}`,
                      }}
                    >
                      {stLabel}
                    </span>
                  </div>

                  <div style={{ fontWeight: 700, fontSize: 13.5, color: "var(--navy)", marginBottom: 2 }}>
                    {o.product}
                  </div>

                  <div
                    style={{
                      fontSize: 11,
                      color: o.description?.trim() ? "#475569" : "#94a3b8",
                      fontStyle: o.description?.trim() ? "normal" : "italic",
                      marginBottom: 3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={o.description?.trim() || "No description provided"}
                  >
                    {o.description?.trim() ? o.description.trim() : "No description provided"}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#64748b" }}>
                    <span>{(o.quantity || 0).toLocaleString()} {o.unit || "pcs"}</span>
                    <span>{o.assigned_delivery_person || "Staff unassigned"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* INDIVIDUAL ORDER DETAIL HEAD (When viewing single order)       */}
      {/* ============================================================== */}
      {!isBatchMode && (
        <div className="detail-head" style={{ marginBottom: 20 }}>
          <div>
            <div className="small-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Truck size={14} color="#4f46e5" /> DISPATCH WORKSPACE · #{order.order_id}
            </div>
            <h1 style={{ marginTop: 4, marginBottom: 2 }} data-testid="dispatch-product-title">{order.product}</h1>
            {/* Order Description Directly Below Product Name - Bold, Prominent, Clean, Non-Boxed (Matching Design section) */}
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: order.description?.trim() ? "#0f172a" : "#94a3b8",
                fontStyle: order.description?.trim() ? "normal" : "italic",
                lineHeight: 1.5,
                margin: "4px 0 10px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
              data-testid="dispatch-order-description-text"
            >
              {order.description?.trim() ? order.description.trim() : "No description provided"}
            </div>
            <p className="muted" style={{ margin: 0 }}>
              {order.client_name} · {(order.quantity || 0).toLocaleString()} units · Due{" "}
              {order.required_delivery_date || "—"}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {order.current_stage !== "Order Received" && (
              <button
                type="button"
                className="outline-btn compact"
                data-testid="dispatch-header-move-back-btn"
                style={{
                  color: "#b45309",
                  borderColor: "#fde68a",
                  background: "#fffbeb",
                  fontWeight: 600,
                  fontSize: 12,
                }}
                onClick={() => setMovingBack(true)}
                title="Move order back to previous stage"
              >
                <RotateCcw size={12} /> Move Back
              </button>
            )}
            <span
              className="badge"
              style={{
                fontSize: 12,
                padding: "5px 12px",
                ...statusBadgeStyle(currentStatus),
              }}
            >
              {currentStatus}
            </span>
            {order.priority && (
              <span
                className={`badge ${
                  order.priority === "Urgent" ? "red" : order.priority === "High" ? "amber" : "blue"
                }`}
                style={{ fontSize: 12, padding: "5px 12px" }}
              >
                {order.priority}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Workflow Progress Strip */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 16px",
          background: "#fff",
          border: "1px solid var(--line)",
          borderRadius: 8,
          marginBottom: 22,
          fontSize: 12.5,
          color: "#475569",
          overflowX: "auto",
        }}
      >
        <span style={{ fontWeight: 700, color: "#0f172a" }}>Workflow:</span>
        <span style={{ color: "#16a34a", fontWeight: 600 }}>1. Ready for Dispatch ✓</span>
        <span style={{ color: "#cbd5e1" }}>→</span>
        <span style={{ color: isDeliveryPersonAssigned ? "#16a34a" : "#2563eb", fontWeight: 600 }}>
          2. Assign Delivery Staff {isDeliveryPersonAssigned ? `(${order?.assigned_delivery_person}) ✓` : "(Pending)"}
        </span>
        <span style={{ color: "#cbd5e1" }}>→</span>
        <span style={{ color: isOut || isDel ? "#16a34a" : "#94a3b8", fontWeight: 600 }}>
          3. Out for Delivery {isOut ? "(In Transit)" : ""}
        </span>
        <span style={{ color: "#cbd5e1" }}>→</span>
        <span style={{ color: isDel ? "#16a34a" : "#94a3b8", fontWeight: 600 }}>
          4. Delivered {isDel ? "✓" : ""}
        </span>
      </div>

      {/* 2-Column CRM Layout */}
      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 22, alignItems: "flex-start" }}>
        {/* LEFT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Card 1: Client & Shipping Details */}
          <section className="panel">
            <div className="panel-title">
              <div>
                <h3 style={{ margin: 0 }}>Client & Shipping Details</h3>
                <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                  Destination contact and delivery destination
                </p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5 }}>
                <span className="muted">Client / Company:</span>
                <b style={{ color: "#0f172a" }}>{order?.client_name || batchData?.client_name}</b>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5 }}>
                <span className="muted">Contact Person:</span>
                <span style={{ fontWeight: 600, color: "#1e293b" }}>{contactPerson}</span>
              </div>

              {canViewClientPhone(user) && (
                <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5, alignItems: "center" }}>
                  <span className="muted">Phone Number:</span>
                  <div>
                    {contactPhone ? (
                      <a
                        href={`tel:${contactPhone}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          color: "#2563eb",
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        <Phone size={13} /> {contactPhone}
                      </a>
                    ) : (
                      <span className="muted">Not specified</span>
                    )}
                  </div>
                </div>
              )}

              {contactEmail && (
                <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5 }}>
                  <span className="muted">Email:</span>
                  <span>{contactEmail}</span>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, fontSize: 13.5, borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
                <span className="muted" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <MapPin size={13} /> Delivery Address:
                </span>
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid var(--line)",
                    color: "#1e293b",
                    lineHeight: 1.45,
                  }}
                >
                  {shippingAddress}
                </div>
              </div>
            </div>
          </section>

          {/* Card 2: Production Information */}
          <section className="panel">
            <div className="panel-title">
              <div>
                <h3 style={{ margin: 0 }}>Production Information</h3>
                <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                  Shop floor handoff and readiness timestamps
                </p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 11, marginTop: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, fontSize: 13 }}>
                <span className="muted">Production Vendor:</span>
                <b style={{ color: "#1e293b" }}>
                  {order?.production_vendor_name || batchData?.production_vendor_name || "—"}
                </b>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, fontSize: 13 }}>
                <span className="muted">Assigned Production User:</span>
                <b style={{ color: (order?.assigned_producer || order?.assigned_production_user) ? "#059669" : "#d97706" }}>
                  {order?.assigned_producer || order?.assigned_production_user || batchData?.orders?.find(o => o.assigned_producer)?.assigned_producer || "Unassigned"}
                </b>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, fontSize: 13 }}>
                <span className="muted">Warehouse / Pickup:</span>
                <b style={{ color: "#1e293b" }}>
                  {order?.warehouse_name || batchData?.warehouse_name || "—"}
                </b>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, fontSize: 13 }}>
                <span className="muted">Production Completed:</span>
                <span style={{ color: "#16a34a", fontWeight: 600 }}>
                  {formatDateTime(order?.production_actual || order?.production_completed_at || order?.ready_for_dispatch_at)}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, fontSize: 13 }}>
                <span className="muted">Ready for Dispatch:</span>
                <span style={{ color: "#2563eb", fontWeight: 600 }}>
                  {formatDateTime(order?.ready_for_dispatch_at || order?.production_actual)}
                </span>
              </div>

              {order?.dispatch_remarks && (
                <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 8, fontSize: 13, borderTop: "1px solid #f1f5f9", paddingTop: 10 }}>
                  <span className="muted">Dispatch Remarks:</span>
                  <span style={{ color: "#475569" }}>{order.dispatch_remarks}</span>
                </div>
              )}
            </div>
          </section>

          {/* Card 3: Item to Dispatch */}
          {order && (
            <section className="panel" data-testid="dispatch-item-panel">
              <div className="panel-title">
                <div>
                  <h3 style={{ margin: 0 }}>Item to Dispatch</h3>
                  <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                    Package quantity, specifications, and delivery commitment
                  </p>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  background: "#f8fafc",
                  border: "1px solid var(--line)",
                  borderRadius: 6,
                  marginTop: 10,
                }}
              >
                <div>
                  <b style={{ fontSize: 14, color: "#0f172a" }}>{order.product}</b>
                  {order.product_code && (
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                      Code: {order.product_code}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
                    {(order.quantity || 0).toLocaleString()}
                  </div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {order.unit || "Units"}
                  </div>
                </div>
              </div>

              {/* Order Description */}
              <div
                style={{
                  marginTop: 10,
                  padding: "10px 12px",
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 6,
                }}
              >
                <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 10, color: "#64748b", display: "block", marginBottom: 3 }}>
                  Order Description
                </span>
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: order.description?.trim() ? "#0f172a" : "#94a3b8",
                    fontStyle: order.description?.trim() ? "normal" : "italic",
                    lineHeight: 1.45,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                  data-testid="dispatch-order-description-text"
                >
                  {order.description?.trim() ? order.description.trim() : "No description provided"}
                </div>
              </div>

              {/* Design Specifications & Packaging Instructions */}
              {(order.design_specs || order.packaging_instructions || order.notes) && (
                <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {(order.design_specs || order.notes) && (
                    <div style={{ padding: "8px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6 }}>
                      <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 9.5, color: "#64748b", display: "block", marginBottom: 2 }}>
                        Specifications &amp; Instructions
                      </span>
                      <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                        {order.design_specs || order.notes}
                      </div>
                    </div>
                  )}
                  {order.packaging_instructions && (
                    <div style={{ padding: "8px 10px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
                      <span style={{ fontWeight: 700, textTransform: "uppercase", fontSize: 9.5, color: "#15803d", display: "block", marginBottom: 2 }}>
                        Packaging Instructions
                      </span>
                      <div style={{ fontSize: 12, color: "#166534", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                        {order.packaging_instructions}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Card 4: Audit Trail / History */}
          <section className="panel" data-testid="dispatch-history-panel">
            <div className="panel-title">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Clock size={16} color="#0284c7" />
                <div>
                  <h3 style={{ margin: 0 }}>Dispatch Stage History</h3>
                  <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                    Audit trail of workflow transitions and stage corrections
                  </p>
                </div>
              </div>
              {order?.current_stage !== "Order Received" && (
                <button
                  type="button"
                  className="outline-btn compact"
                  style={{ color: "#b45309", borderColor: "#fde68a", background: "#fffbeb", fontSize: 11.5 }}
                  onClick={() => setMovingBack(true)}
                  data-testid="dispatch-history-move-back-btn"
                >
                  <RotateCcw size={11} /> Move Back
                </button>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <OrderHistoryTrail history={order?.history || []} />
            </div>
          </section>
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Card 1: Delivery Staff Assignment */}
          <section className="panel">
            <div className="panel-title">
              <div>
                <h3 style={{ margin: 0 }}>1. Delivery Person</h3>
                <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                  Assign delivery personnel to hand over order
                </p>
              </div>
              <span className={`badge ${isDeliveryPersonAssigned ? "green" : "amber"}`}>
                {isDeliveryPersonAssigned ? "Assigned" : "Required"}
              </span>
            </div>

            {isDeliveryPersonAssigned && !isChangingAgent ? (
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                <div
                  style={{
                    padding: "14px",
                    background: "#f8fafc",
                    border: "1px solid var(--line)",
                    borderRadius: 6,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <span className="muted" style={{ fontSize: 11, display: "block" }}>
                      Assigned Delivery Person
                    </span>
                    <b style={{ fontSize: 15, color: "#0f172a", display: "block", marginTop: 2 }}>
                      {order.assigned_delivery_person}
                    </b>
                    {order.delivery_person_phone && (
                      <span style={{ fontSize: 12.5, color: "#475569", display: "block", marginTop: 2 }}>
                        📞 {order.delivery_person_phone}
                      </span>
                    )}
                  </div>

                  {canAct && (
                    <button
                      type="button"
                      className="outline-btn"
                      style={{ padding: "6px 12px", fontSize: 12 }}
                      onClick={() => setIsChangingAgent(true)}
                    >
                      Change
                    </button>
                  )}
                </div>

                <div
                  style={{
                    padding: "10px 12px",
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: 6,
                    fontSize: 12.5,
                    color: "#166534",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <CheckCircle2 size={16} color="#16a34a" />
                  <span>Assigned to Delivery staff.</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleAssignDeliveryPerson} style={{ marginTop: 14 }}>
                <div className="dispatch-field" style={{ marginBottom: 14 }}>
                  <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 6, color: "#1e293b" }}>
                    Select Delivery Person <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <select
                    className="dispatch-control"
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    required
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--line)",
                      fontSize: 13.5,
                      background: "#fff",
                      color: "#0f172a",
                    }}
                  >
                    {deliveryAgents.length === 0 ? (
                      <option value="" disabled>No Delivery Person available</option>
                    ) : (
                      <option value="">-- Choose Delivery Person --</option>
                    )}
                    {deliveryAgents.map((agent) => (
                      <option key={agent.user_id || agent.name} value={agent.user_id || agent.name}>
                        {agent.name} {agent.phone ? `(${agent.phone})` : ""}
                      </option>
                    ))}
                    <option value="custom">+ Enter New Delivery Person</option>
                  </select>
                </div>

                {selectedAgentId === "custom" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                    <div>
                      <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12, marginBottom: 4, color: "#1e293b" }}>
                        Name *
                      </label>
                      <input
                        className="dispatch-control"
                        type="text"
                        placeholder="e.g. Suresh Kumar"
                        value={customAgentName}
                        onChange={(e) => setCustomAgentName(e.target.value)}
                        required
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
                      />
                    </div>
                    <div>
                      <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12, marginBottom: 4, color: "#1e293b" }}>
                        Phone
                      </label>
                      <input
                        className="dispatch-control"
                        type="text"
                        placeholder="e.g. +91 98201 55667"
                        value={customAgentPhone}
                        onChange={(e) => setCustomAgentPhone(e.target.value)}
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
                      />
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={busyAction === "assign" || !selectedAgentId}
                    style={{
                      flex: 1,
                      height: 40,
                      fontSize: 13,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                    }}
                  >
                    <UserCheck size={15} />
                    {busyAction === "assign" ? "Saving…" : isChangingAgent ? "Update Delivery Person" : "Confirm Delivery Person"}
                  </button>
                  {isChangingAgent && (
                    <button
                      type="button"
                      className="outline-btn"
                      onClick={() => setIsChangingAgent(false)}
                      style={{ height: 40 }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            )}
          </section>

          {/* Card 2: Challan & Delivery Dispatch Actions */}
          <section className="panel">
            <div className="panel-title">
              <div>
                <h3 style={{ margin: 0 }}>2. Out for Delivery</h3>
                <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                  Dispatch package directly to Delivery section (Challan optional)
                </p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}>
              {/* Challan status */}
              <div
                style={{
                  padding: "12px",
                  background: order?.challan_number ? "#f0fdf4" : "#f8fafc",
                  border: `1px solid ${order?.challan_number ? "#bbf7d0" : "#e2e8f0"}`,
                  borderRadius: 6,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span className="muted" style={{ fontSize: 11, display: "block" }}>Delivery Challan</span>
                    <b style={{ fontSize: 13.5, color: "#0f172a" }}>
                      {order?.challan_number ? `#${order.challan_number}` : "Not generated"}
                    </b>
                  </div>

                  {order?.challan_number && challanDoc ? (
                    <button
                      type="button"
                      className="outline-btn compact"
                      onClick={() => downloadDocument(challanDoc.document_id, challanDoc.name)}
                      style={{ fontSize: 11.5, display: "flex", alignItems: "center", gap: 4 }}
                      data-testid="download-challan-btn"
                    >
                      <Download size={12} /> Download PDF
                    </button>
                  ) : canAct ? (
                    <button
                      type="button"
                      className="outline-btn compact"
                      onClick={() => handleGenerateChallan(order?.order_id)}
                      disabled={Boolean(busyAction)}
                      style={{ fontSize: 11.5 }}
                      data-testid="generate-challan-btn"
                    >
                      <FileText size={12} /> Generate Challan
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Action Buttons */}
              {canAct && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                  {!isOut && !isDel && (
                    <button
                      type="button"
                      className="primary-btn wide"
                      style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
                      onClick={() => setShowOutForDeliveryModal(true)}
                      disabled={Boolean(busyAction)}
                      data-testid="single-out-for-delivery-btn"
                    >
                      <Truck size={14} /> Mark Out for Delivery
                    </button>
                  )}

                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* ============================================================== */}
      {/* ALL PRODUCTS OVERVIEW TABLE (Inside Bulk Batch)                */}
      {/* ============================================================== */}
      {isBatchMode && (
        <section className="panel" style={{ marginTop: 20 }}>
          <div className="panel-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h3 style={{ margin: 0 }}>All Batch Products Overview</h3>
              <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                Products in Bulk Request #{batchData.batch_id}
              </p>
            </div>
          </div>

          <div className="table-wrap" style={{ marginTop: 10 }}>
            <table data-testid="batch-dispatch-products-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Production Vendor</th>
                  <th>Assigned Production User</th>
                  <th>Warehouse</th>
                  <th>Challan</th>
                  <th>Delivery Staff</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {batchData.orders.map((o) => {
                  const isOutP = o.dispatch_status === "Out for Delivery" || o.delivery_status === "Out for Delivery";
                  const isDelP = o.delivery_status === "Delivered" || o.dispatch_status === "Delivered";
                  const stLabel = isDelP ? "Delivered" : isOutP ? "Out for Delivery" : o.assigned_delivery_person ? "Assigned" : "Ready for Dispatch";
                  const bTheme = statusBadgeStyle(stLabel);
                  const isSelected = order?.order_id === o.order_id;

                  return (
                    <tr
                      key={o.order_id}
                      style={{ background: isSelected ? "#f8fafc" : undefined }}
                      data-testid={`batch-dispatch-row-${o.order_id}`}
                    >
                      <td><b className="mono" style={{ color: "#2563eb" }}>#{o.order_id}</b></td>
                      <td>
                        <b>{o.product}</b>
                        {o.product_code && <small style={{ display: "block" }}>{o.product_code}</small>}
                        <div
                          style={{
                            fontSize: 11,
                            color: o.description?.trim() ? "#475569" : "#94a3b8",
                            fontStyle: o.description?.trim() ? "normal" : "italic",
                            marginTop: 2,
                            lineHeight: 1.3,
                          }}
                        >
                          {o.description?.trim() ? o.description.trim() : "No description provided"}
                        </div>
                        {o.packaging_instructions && (
                          <div style={{ fontSize: 10, color: "#0284c7", marginTop: 1 }}>
                            📦 {o.packaging_instructions}
                          </div>
                        )}
                      </td>
                      <td>{(o.quantity || 0).toLocaleString()} {o.unit || "pcs"}</td>
                      <td><small>{o.production_vendor_name || "—"}</small></td>
                      <td>
                        <small style={{ color: (o.assigned_producer || o.assigned_production_user) ? "#059669" : "#d97706", fontWeight: (o.assigned_producer || o.assigned_production_user) ? 600 : "normal" }}>
                          {o.assigned_producer || o.assigned_production_user || "Unassigned"}
                        </small>
                      </td>
                      <td><small>{o.warehouse_name || "—"}</small></td>
                      <td><small>{o.challan_number ? `#${o.challan_number}` : "—"}</small></td>
                      <td><small>{o.assigned_delivery_person || "Unassigned"}</small></td>
                      <td>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "2px 7px",
                            borderRadius: 4,
                            color: bTheme.color,
                            background: bTheme.bg,
                            border: `1px solid ${bTheme.border}`,
                          }}
                        >
                          {stLabel}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          type="button"
                          className="outline-btn compact"
                          onClick={() => handleSelectProduct(o)}
                          style={{ fontSize: 11 }}
                        >
                          {isSelected ? "Active" : "Inspect"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Out For Delivery Modal (Single Order) */}
      {showOutForDeliveryModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            className="panel"
            style={{
              width: "100%",
              maxWidth: 460,
              background: "#fff",
              borderRadius: 10,
              padding: 24,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 17, color: "var(--navy)" }}>Mark Out for Delivery</h3>
              <button type="button" className="icon-btn" onClick={() => setShowOutForDeliveryModal(false)}>
                <X size={16} />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleMarkOutForDelivery(); }}>
              <div className="dispatch-field" style={{ marginBottom: 12 }}>
                <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>
                  Transporter / Delivery Person
                </label>
                <input
                  className="dispatch-control"
                  type="text"
                  placeholder="e.g. Ajay Sharma / BlueDart"
                  value={transporterName}
                  onChange={(e) => setTransporterName(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
                />
              </div>

              <div className="dispatch-field" style={{ marginBottom: 16 }}>
                <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>
                  Tracking / LR Number (Optional)
                </label>
                <input
                  className="dispatch-control"
                  type="text"
                  placeholder="e.g. TRK-998822"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" className="outline-btn" onClick={() => setShowOutForDeliveryModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary-btn" style={{ background: "#7c3aed", borderColor: "#7c3aed" }}>
                  Confirm Out for Delivery
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Batch Out for Delivery Modal */}
      {showBatchOutModal && batchData && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            className="panel"
            style={{
              width: "100%",
              maxWidth: 480,
              background: "#fff",
              borderRadius: 10,
              padding: 24,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 17, color: "var(--navy)" }}>
                Batch Out for Delivery · #{batchData.batch_id}
              </h3>
              <button type="button" className="icon-btn" onClick={() => setShowBatchOutModal(false)}>
                <X size={16} />
              </button>
            </div>

            <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 14 }}>
              Mark all <b>{batchData.orders.length} products</b> in Bulk Request #{batchData.batch_id} out for delivery.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleBatchOutForDelivery({
                  transporter: transporterName,
                  tracking: trackingNumber,
                });
              }}
            >
              <div className="dispatch-field" style={{ marginBottom: 12 }}>
                <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>
                  Transporter / Driver
                </label>
                <input
                  className="dispatch-control"
                  type="text"
                  placeholder="e.g. Ajay Sharma / BlueDart"
                  value={transporterName}
                  onChange={(e) => setTransporterName(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
                />
              </div>

              <div className="dispatch-field" style={{ marginBottom: 16 }}>
                <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>
                  Tracking / LR Number (Optional)
                </label>
                <input
                  className="dispatch-control"
                  type="text"
                  placeholder="e.g. TRK-BULK-9900"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" className="outline-btn" onClick={() => setShowBatchOutModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary-btn" style={{ background: "#7c3aed", borderColor: "#7c3aed" }}>
                  Confirm Batch Out for Delivery
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Batch Assign Delivery Modal */}
      {showBatchAssignModal && batchData && (
        <BatchAssignDeliveryModalInner
          batch={batchData}
          deliveryAgents={deliveryAgents}
          isOpen={showBatchAssignModal}
          onClose={() => setShowBatchAssignModal(false)}
          onSubmit={handleBatchAssign}
        />
      )}

      {/* Move Back Stage Modal */}
      {movingBack && (
        <MoveBackStageModal
          order={order || (batchData?.orders ? batchData.orders[0] : null)}
          isOpen={movingBack}
          onClose={() => setMovingBack(false)}
          onSuccess={() => {
            loadOrder();
            onChange && onChange();
          }}
          user={user}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------
// Inner Modal for Batch Assign Delivery Staff
// -------------------------------------------------------------
function BatchAssignDeliveryModalInner({ batch, deliveryAgents, isOpen, onClose, onSubmit }) {
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [customName, setCustomName] = useState("");
  const [customPhone, setCustomPhone] = useState("");
  const [remarks, setRemarks] = useState("");

  if (!isOpen || !batch) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.6)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        className="panel"
        style={{
          width: "100%",
          maxWidth: 480,
          background: "#fff",
          borderRadius: 10,
          padding: 24,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 17, color: "var(--navy)" }}>
            Assign Delivery Person · #{batch.batch_id}
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 14 }}>
          Assign delivery staff for all <b>{batch.orders.length} products</b> in Bulk Request #{batch.batch_id}.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit({
              agentId: selectedAgentId,
              customName,
              customPhone,
              remarks,
            });
          }}
        >
          <div className="dispatch-field" style={{ marginBottom: 14 }}>
            <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 6 }}>
              Select Delivery Person <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <select
              className="dispatch-control"
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              required
              style={{ width: "100%", padding: "9px 12px", borderRadius: 6, border: "1px solid var(--line)" }}
            >
              <option value="">-- Choose Delivery Person --</option>
              {deliveryAgents.map((agent) => (
                <option key={agent.user_id || agent.name} value={agent.user_id || agent.name}>
                  {agent.name} {agent.phone ? `(${agent.phone})` : ""}
                </option>
              ))}
              <option value="custom">+ Enter New Delivery Person</option>
            </select>
          </div>

          {selectedAgentId === "custom" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div>
                <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                  Name *
                </label>
                <input
                  className="dispatch-control"
                  type="text"
                  placeholder="e.g. Suresh Kumar"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  required
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
                />
              </div>
              <div>
                <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                  Phone
                </label>
                <input
                  className="dispatch-control"
                  type="text"
                  placeholder="e.g. +91 98201 55667"
                  value={customPhone}
                  onChange={(e) => setCustomPhone(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
                />
              </div>
            </div>
          )}

          <div className="dispatch-field" style={{ marginBottom: 18 }}>
            <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 4 }}>
              Remarks (Optional)
            </label>
            <input
              className="dispatch-control"
              type="text"
              placeholder="e.g. Route assignment"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="outline-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              disabled={!selectedAgentId}
              style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
            >
              Confirm Batch Assignment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
